// Интерфейс: вкладки форматов, предпросмотр на canvas, панель ячейки с поиском, сохранение PNG.

import { MODES, MODE_IDS, browserStorage } from './core/state.js';
import { createBoard } from './core/board.js';
import { initSiteHeader } from '../../site-chrome/menu.js';
import { W, H, renderPoster, hitTile, fontsReady } from './core/render.js';
import { searchBooks, searchPeople, bookEditions } from './core/search.js';
import { catalogFor, catalogMatches } from './core/catalog.js';
import * as images from './core/images.js';
import { EXAMPLES } from './core/examples.js';

const tipsList = () => catalogFor(board.mode).items;

// Вся логика сетки (ячейки, выбор, сохранение, загрузка обложек) — в Board; здесь только интерфейс.
const board = createBoard({ storage: browserStorage, loadImage: images.fromUrl, preload: images.preload });
// Чтение состояния для отрисовки; менять его можно только операциями Board.
const state = { get mode() { return board.mode; }, get theme() { return board.theme; }, get sel() { return board.sel; } };
// Режим из адреса: /9-knig/pisateli/ или #pisateli (на главной странице).
const slug = location.pathname.replace(/\/+$/, '').split('/').pop();
const hashMode = Object.keys(MODES).find((id) => MODES[id].hash === slug || '#' + MODES[id].hash === location.hash);
if (hashMode) board.setMode(hashMode);

const $ = (sel, root = document) => root.querySelector(sel);
function el(tag, attrs = {}, ...kids) {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === false) continue;
    if (k === 'class') e.className = v;
    else if (k.startsWith('on')) e.addEventListener(k.slice(2), v);
    else e.setAttribute(k, v === true ? '' : v);
  }
  for (const kid of kids.flat()) if (kid != null) e.append(kid.nodeType ? kid : document.createTextNode(kid));
  return e;
}

// ---------------------------------------------------------------- шапка сайта (site-chrome)
initSiteHeader();

// ---------------------------------------------------------------- toast
function toast(text) {
  const box = $('#toasts');
  const t = el('div', { class: 'toast' }, text);
  box.append(t);
  setTimeout(() => t.remove(), 4500);
}

// ---------------------------------------------------------------- события Board → интерфейс
window.addEventListener('pagehide', () => board.flush());
board.subscribe((ev) => {
  switch (ev.kind) {
    case 'sel': case 'theme': case 'reset': editIdx = null; renderPanel(); draw(); break;
    case 'mode':
      history.replaceState(null, '', location.pathname + '#' + MODES[board.mode].hash);
      renderPanel(); draw(); break;
    case 'title': case 'cover': draw(); break;
    case 'cell': syncSlots(); draw(); break;           // набор текста: поля не трогаем, чтобы не сбить ввод
    case 'place':
      editIdx = ev.idx;   // «Другая обложка» остаётся у только что выбранной книги, хотя выбор ушёл дальше
      syncPanel(); draw();
      if (!wide.matches) canvas.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      break;
    case 'cover-failed':
      toast(ev.replacing ? 'Эту обложку загрузить не удалось, попробуйте другую.'
        : 'Картинку загрузить не удалось, оставил цветную плитку. Можно загрузить своё фото.');
      break;
    case 'save-failed':
      toast('Браузер не даёт сохранить сетку: после закрытия страницы она пропадёт. Скачайте PNG до этого.');
      break;
  }
});

// ---------------------------------------------------------------- предпросмотр
const stage = $('#stage');
const canvas = el('canvas', { class: 'poster', width: W, height: H, role: 'img', tabindex: '-1' });
stage.append(canvas);
const ctx = canvas.getContext('2d');
let raf = 0;

function draw() {
  cancelAnimationFrame(raf);
  raf = requestAnimationFrame(() => {
    const d = board.doc;
    renderPoster(ctx, d, { theme: state.theme, selected: state.sel, hints: true });
    canvas.setAttribute('aria-label', `${d.title}. ` + d.cells.map((c, i) => `${i + 1}: ${c.title || 'пусто'}`).join('; '));
  });
}
canvas.addEventListener('click', (e) => {
  const r = canvas.getBoundingClientRect();
  const i = hitTile(((e.clientX - r.left) / r.width) * W, ((e.clientY - r.top) / r.height) * H);
  if (i >= 0) { board.select(i); $('#q') && $('#q').focus({ preventScroll: false }); }
});

// ---------------------------------------------------------------- панель
const panel = $('#panel');
let searchSeq = 0;
let searchCtl = null;

function renderPanel() {
  const mode = MODES[state.mode];
  const d = board.doc;
  const cell = d.cells[state.sel];
  const hadFocus = document.activeElement && document.activeElement.id;
  panel.textContent = '';

  const tabs = el('div', { class: 'seg', role: 'group', 'aria-label': 'Формат' },
    MODE_IDS.map((id) => el('button', {
      type: 'button', class: 'seg-btn', 'aria-pressed': String(id === state.mode),
      onclick: () => board.setMode(id),
    }, MODES[id].label)));

  const title = el('input', {
    id: 'title', class: 'field', type: 'text', maxlength: '80', value: d.title,
    oninput: (e) => board.setTitle(e.target.value),
  });

  const theme = el('div', { class: 'seg', role: 'group', 'aria-label': 'Оформление' },
    [['dark', 'Тёмное'], ['light', 'Светлое']].map(([id, label]) => el('button', {
      type: 'button', class: 'seg-btn', 'aria-pressed': String(state.theme === id),
      onclick: () => board.setTheme(id),
    }, label)));

  const slots = el('div', { class: 'slots', role: 'group', 'aria-label': 'Ячейки' },
    d.cells.map((c, i) => el('button', {
      type: 'button', class: 'slot', 'aria-pressed': String(i === state.sel),
      'aria-label': `Ячейка ${i + 1}: ${c.title || 'пусто'}`,
      onclick: () => board.select(i),
    }, el('span', { class: 'slot-t' }, c.title || 'Пусто'))));

  const q = el('input', {
    id: 'q', class: 'field', type: 'search', autocomplete: 'off', placeholder: mode.search,
    'aria-describedby': 'q-hint', role: 'combobox', 'aria-controls': 'results', 'aria-expanded': 'false',
    oninput: onQuery, onkeydown: onQueryKey,
  });
  const results = el('ul', { class: 'results', id: 'results', role: 'listbox', 'aria-label': 'Результаты поиска', onkeydown: onResultsKey });

  const nameIn = el('input', {
    id: 'cell-title', class: 'field', type: 'text', maxlength: '120', value: cell.title,
    oninput: (e) => board.edit({ title: e.target.value }),
  });
  const subIn = el('input', {
    id: 'cell-sub', class: 'field', type: 'text', maxlength: '120', value: cell.sub,
    oninput: (e) => board.edit({ sub: e.target.value }),
  });
  const file = el('input', { type: 'file', accept: 'image/*', class: 'file-input', id: 'file', onchange: onFile });

  panel.append(...[
    group('Формат', tabs),
    group('Заголовок', title, 'title'),
    group('Оформление', theme),
    el('div', { class: 'row tools' },
      el('button', { type: 'button', class: 'btn', onclick: fillExample }, 'Заполнить примером'),
      el('button', { type: 'button', class: 'btn', onclick: clearAll }, 'Очистить сетку')),
    group('Выбранная ячейка', slots),
    el('div', { class: 'cell-box' },
      el('label', { class: 'lbl', for: 'q' }, 'Найти'),
      q,
      el('p', { class: 'hint', id: 'q-hint' }, state.mode === 'books'
        ? 'Ищем в Apple Books и Open Library. Если подходящей обложки нет, останется цветная плитка, или загрузите своё фото.'
        : 'Ищем в Википедии, портрет берём оттуда же. Нет портрета — будет плитка, или загрузите своё фото.'),
      results,
      el('div', { id: 'editions-slot' }, editionsBlock()),
      // Ручной ввод — запасной путь: сначала поиск и подсказки. У поэтов «Стихотворение» — основное поле, оно снаружи.
      el('details', { class: 'manual', 'data-kind': 'manual', open: manualOpen, ontoggle: (e) => { manualOpen = e.target.open; } },
        el('summary', {}, `Если ${mode.notFound} нет в каталоге`),
        el('div', { class: 'manual-body' },
          el('label', { class: 'lbl', for: 'cell-title' }, mode.nameLabel), nameIn,
          state.mode === 'books' ? [el('label', { class: 'lbl', for: 'cell-sub' }, mode.subLabel), subIn] : null)),
      state.mode === 'poets' ? [el('label', { class: 'lbl', for: 'cell-sub' }, mode.subLabel), subIn] : null,
      el('div', { class: 'row' },
        el('label', { class: 'btn', for: 'file' }, 'Загрузить своё фото'), file,
        el('button', { type: 'button', class: 'btn', onclick: () => { board.removeCover(); toast('Картинку убрал, осталась плитка'); } }, 'Убрать картинку'),
        el('button', { type: 'button', class: 'btn', onclick: () => board.clearCell() }, 'Очистить ячейку'))),
    el('div', { class: 'row end' },
      el('button', { type: 'button', class: 'btn btn-primary', id: 'save', onclick: onSavePng }, 'Скачать PNG')),
  ].filter(Boolean));
  placeTips();
  if (hadFocus) { const f = document.getElementById(hadFocus); if (f && f !== document.activeElement) f.focus(); }
}

// ---------------------------------------------------------------- подсказки: список The Guardian
// «100 лучших романов всех времён» (12.05.2026). В файле только названия и ссылки на обложки,
// сами картинки грузятся из Apple Books и Open Library по клику, как и при обычном поиске.
let manualOpen = false;      // спойлер «Если книги нет в каталоге»
let tipsOpen = false;        // телефон: блок внутри панели, по умолчанию свёрнут
let tipsExpanded = false;    // десктоп: блок под превью, виден первый ряд, «Показать все» раскрывает
let tipsEl = null;
const wide = window.matchMedia('(min-width: 881px)');
const tipsSlot = $('#tips-slot');

function toggleTipsExpanded() {
  tipsExpanded = !tipsExpanded;
  if (tipsEl) {
    tipsEl.classList.toggle('expanded', tipsExpanded);
    const b = $('.tips-more', tipsEl);
    b.setAttribute('aria-expanded', String(tipsExpanded));
    b.textContent = tipsExpanded ? 'Свернуть' : 'Показать все';
  }
}

// Десктоп: блок под превью, перед текстом страницы. Телефон: внутри панели, перед кнопками.
function placeTips() {
  tipsSlot.textContent = '';
  tipsEl = tipsBlock();
  if (wide.matches) {
    tipsEl.open = true;
    tipsEl.classList.toggle('expanded', tipsExpanded);
    tipsSlot.append(tipsEl);
    const grid = $('.tips-grid', tipsEl);
    const fit = () => { const t = $('.tip', grid); if (t) grid.style.setProperty('--row-h', t.offsetHeight + 'px'); };
    fit();
    if ('ResizeObserver' in window) new ResizeObserver(fit).observe(grid);
  } else {
    tipsEl.open = tipsOpen;
    panel.insertBefore(tipsEl, $('.row.tools', panel));
  }
}
wide.addEventListener('change', () => { if (tipsEl) placeTips(); });

// Подпись источника из данных каталога: строка или {href, text}
const sourceNodes = (parts) => parts.map((p) => (typeof p === 'string' ? p : el('a', { href: p.href, rel: 'noopener' }, p.text)));

function tipsBlock() {
  const { items: list, attribution: tt } = catalogFor(state.mode);
  const have = new Set(board.doc.cells.map((c) => c.title));
  const grid = el('div', { class: 'tips-grid' }, list.map((b, i) => {
    const thumb = b.thumbUrl ? el('img', {
      src: b.thumbUrl, alt: '', loading: 'lazy', referrerpolicy: 'no-referrer', width: '64', height: '96',
      onerror: (e) => e.target.remove(),
    }) : null;
    return el('button', {
      type: 'button', class: 'tip', 'data-i': String(i), 'aria-pressed': String(have.has(b.title)),
      title: b.sub ? `${b.title}, ${b.sub}` : b.title, 'aria-label': b.sub ? `${b.title}, ${b.sub}` : b.title, onclick: () => pickTop(i),
    }, thumb, el('span', { class: 'tip-t' }, b.title));
  }));
  const det = el('details', { class: 'tips', ontoggle: (e) => { if (!wide.matches) tipsOpen = e.target.open; } },
    el('summary', {}, tt.title),
    el('p', { class: 'hint' }, tt.hint),
    grid,
    el('button', { type: 'button', class: 'tips-more', 'aria-expanded': String(tipsExpanded), onclick: toggleTipsExpanded },
      tipsExpanded ? 'Свернуть' : 'Показать все'),
    el('p', { class: 'hint' }, ...sourceNodes(tt.source)));
  if (tipsOpen) det.setAttribute('open', '');
  return det;
}

// Лёгкое обновление панели без пересборки: подсказки выбирают много книг подряд,
// и сетка с прокруткой не должна сбрасываться.
function syncSlots() {
  const d = board.doc;
  panel.querySelectorAll('.slot').forEach((b, i) => {
    const t = d.cells[i].title;
    b.setAttribute('aria-pressed', String(i === state.sel));
    b.setAttribute('aria-label', `Ячейка ${i + 1}: ${t || 'пусто'}`);
    $('.slot-t', b).textContent = t || 'Пусто';
  });
}

function syncPanel() {
  const d = board.doc;
  const cell = d.cells[state.sel];
  syncSlots();
  const nt = $('#cell-title'); if (nt) nt.value = cell.title;
  const ns = $('#cell-sub'); if (ns) ns.value = cell.sub;
  const have = new Set(d.cells.map((c) => c.title));
  document.querySelectorAll('.tip[data-i]').forEach((b) => b.setAttribute('aria-pressed', String(have.has(tipsList()[Number(b.dataset.i)].title))));
  // «Другая обложка» зависит от выбранной ячейки
  const slot = $('#editions-slot');
  if (slot) { slot.textContent = ''; const eb = editionsBlock(); if (eb) slot.append(eb); }
}

// Подсказка из каталога → в выбранную ячейку; выбор переходит к следующей пустой.
function pickTop(i) {
  board.place(tipsList()[i], { advance: true });
}

function group(label, control, forId) {
  return el('div', { class: 'group' }, el(forId ? 'label' : 'span', { class: 'lbl', for: forId }, label), control);
}

// ---------------------------------------------------------------- поиск
// Две волны: сразу (без сети) — совпадения из нашего каталога подсказок; через 0,4 с после
// последней буквы — Apple Books и Open Library (книги) или Википедия (писатели, поэты).
let qTimer = 0;
const normQ = (t) => String(t).toLowerCase().replace(/ё/g, 'е').replace(/\s+/g, ' ').trim();

function localMatches(text) {
  return catalogMatches(board.mode, text, 6);
}

function onQuery(e) {
  clearTimeout(qTimer);
  const text = e.target.value.trim();
  const box = $('#results');
  if (searchCtl) searchCtl.abort();
  searchSeq++;
  if (text.length < 2) { box.textContent = ''; setExpanded(false); return; }
  // мгновенно: каталог + пометка, что сеть ещё ищет
  renderResults(box, text, localMatches(text), [], 'Ищу в сети…');
  qTimer = setTimeout(() => runSearch(text), 400);
}

function setExpanded(v) { const q = $('#q'); if (q) q.setAttribute('aria-expanded', String(v)); }

function resultItem(it) {
  const thumb = it.thumbUrl
    ? el('img', { class: 'res-img', src: it.thumbUrl, alt: '', loading: 'lazy', referrerpolicy: 'no-referrer', width: '36', height: '54', onerror: (e) => e.target.replaceWith(el('span', { class: 'res-img res-img-none' })) })
    : el('span', { class: 'res-img res-img-none' });
  return el('li', { role: 'none' }, el('button', { type: 'button', class: 'res', role: 'option', onclick: () => pick(it) },
    thumb,
    el('span', { class: 'res-body' },
      el('span', { class: 'res-t' }, it.title),
      el('span', { class: 'res-s' }, [it.sub || it.note, it.source, it.imgUrl ? '' : 'без картинки'].filter(Boolean).join(' · ')))));
}

// local — из каталога (уже с обложками), net — из сети; status — строка состояния внизу.
function renderResults(box, text, local, net, status, notes = []) {
  box.textContent = '';
  const seen = new Set();
  const key = (it) => `${normQ(it.title)}|${normQ(it.sub || '')}`;
  for (const it of [...local, ...net]) {
    if (seen.has(key(it))) continue;
    seen.add(key(it));
    box.append(resultItem(it));
  }
  for (const n of notes) box.append(el('li', { class: 'res-note', role: 'none' }, n));
  if (status) box.append(el('li', { class: 'res-note', role: 'none' }, status));
  // «Добавить как есть» — всегда последним: ничего не нашлось или нужной записи нет в выдаче
  box.append(el('li', { role: 'none' }, el('button', { type: 'button', class: 'res res-add', role: 'option', onclick: () => addAsIs(text) },
    el('span', { class: 'res-body' },
      el('span', { class: 'res-t' }, `Добавить «${text}» как есть`),
      el('span', { class: 'res-s' }, state.mode === 'books' ? 'Цветная плитка с названием; автора можно вписать ниже' : 'Цветная плитка с именем, фото можно загрузить')))));
  setExpanded(true);
}

async function runSearch(text) {
  const box = $('#results');
  if (!box) return;
  if (searchCtl) searchCtl.abort();
  searchCtl = new AbortController();
  const seq = ++searchSeq;
  const mode = state.mode;
  const local = localMatches(text);
  try {
    const list = await (mode === 'books' ? searchBooks : searchPeople)(text, searchCtl.signal);
    if (seq !== searchSeq || !box.isConnected) return;
    renderResults(box, text, local, list, list.length || local.length ? '' : 'Ничего не нашлось.', list.notes || []);
  } catch (e) {
    if (seq !== searchSeq || (e && e.name === 'AbortError')) return;
    renderResults(box, text, local, [], e.message || 'Поиск недоступен. Можно ввести название вручную.');
  }
}

// «Добавить как есть»: название = введённый текст; автора (для книг) можно вписать в спойлере
function addAsIs(text) {
  board.place({ title: text }, { advance: false });
  $('#q').value = '';
  $('#results').textContent = '';
  setExpanded(false);
  if (state.mode === 'books') {   // автор — следующее, что захочется вписать
    manualOpen = true;
    const det = $('details[data-kind=manual]'); if (det) det.open = true;
    const sub = $('#cell-sub'); if (sub) sub.focus();
  }
}

// Клавиатура: ↓ из поля — к первому результату, Enter — выбрать первый, Esc — очистить.
function resultButtons() { return [...document.querySelectorAll('#results .res')]; }
function onQueryKey(e) {
  if (e.key === 'ArrowDown') {
    const b = resultButtons()[0];
    if (b) { e.preventDefault(); b.focus(); }
  } else if (e.key === 'Enter') {
    const b = resultButtons()[0];
    if (b) { e.preventDefault(); b.click(); }
  } else if (e.key === 'Escape') {
    e.target.value = ''; $('#results').textContent = ''; setExpanded(false);
  }
}
function onResultsKey(e) {
  const bs = resultButtons();
  const i = bs.indexOf(document.activeElement);
  if (i < 0) return;
  if (e.key === 'ArrowDown') { e.preventDefault(); (bs[i + 1] || bs[i]).focus(); }
  else if (e.key === 'ArrowUp') { e.preventDefault(); (i === 0 ? $('#q') : bs[i - 1]).focus(); }
  else if (e.key === 'Escape') { $('#q').focus(); }
}

// ---------------------------------------------------------------- другая обложка (книги)
let editionsOpen = false;
let editIdx = null;   // ячейка, к которой относится «Другая обложка»: последняя поставленная или выбранная
const editionsCache = new Map();

function editionsBlock() {
  const cell = board.doc.cells[editIdx ?? state.sel];
  if (state.mode !== 'books' || !cell.title) return null;
  const box = el('div', { class: 'editions' });
  const det = el('details', { class: 'manual', 'data-kind': 'editions', open: editionsOpen, ontoggle: (e) => { editionsOpen = e.target.open; if (editionsOpen) loadEditions(box); } },
    el('summary', {}, 'Другая обложка'),
    el('div', { class: 'manual-body' },
      el('p', { class: 'hint' }, `Другие издания книги «${cell.title}» из Apple Books и Open Library: нажмите на обложку, она встанет в ячейку.`),
      box));
  if (editionsOpen) loadEditions(box);
  return det;
}

async function loadEditions(box) {
  const idx = editIdx ?? state.sel;
  const cell = board.doc.cells[idx];
  const key = `${normQ(cell.title)}|${normQ(cell.sub)}`;
  box.textContent = '';
  box.append(el('p', { class: 'hint' }, 'Ищу издания…'));
  try {
    let list = editionsCache.get(key);
    if (!list) { list = await bookEditions(cell.title, cell.sub); editionsCache.set(key, list); }
    if (!box.isConnected) return;
    box.textContent = '';
    if (!list.length) { box.append(el('p', { class: 'hint' }, 'Других изданий не нашлось. Можно загрузить своё фото.')); return; }
    const grid = el('div', { class: 'tips-grid editions-grid' });
    for (const v of list) {
      grid.append(el('button', {
        type: 'button', class: 'tip', title: `${v.title}, ${v.source}`, 'aria-label': `Обложка: ${v.title}, ${v.source}`,
        'aria-pressed': 'false', onclick: () => board.attachCover({ url: v.imgUrl }, idx),
      }, el('img', { src: v.thumbUrl || v.imgUrl, alt: '', loading: 'lazy', referrerpolicy: 'no-referrer', width: '64', height: '96', onerror: (e) => e.target.closest('.tip').remove() })));
    }
    box.append(grid);
  } catch {
    if (box.isConnected) { box.textContent = ''; box.append(el('p', { class: 'hint' }, 'Не удалось найти издания. Попробуйте позже или загрузите своё фото.')); }
  }
}

// Результат поиска → в выбранную ячейку, выбор переходит к следующей пустой; поле поиска остаётся
// под рукой для следующей книги (на телефоне фокус не берём: не открывать клавиатуру).
function pick(it) {
  $('#q').value = '';
  $('#results').textContent = '';
  setExpanded(false);
  board.place({ title: it.title, sub: it.sub, imgUrl: it.imgUrl }, { advance: true });
  if (wide.matches) $('#q').focus();
}

async function onFile(e) {
  const f = e.target.files && e.target.files[0];
  e.target.value = '';
  if (!f) return;
  try {
    board.attachCover({ data: await images.fromFile(f) });
  } catch (err) {
    toast(err.message || 'Не удалось прочитать файл.');
  }
}

// ---------------------------------------------------------------- пример и очистка
const filled = (d) => d.cells.some((c) => c.title || c.sub || c.img);

function clearAll() {
  if (filled(board.doc) && !window.confirm('Очистить все девять ячеек?')) return;
  board.clear();
}

async function fillExample() {
  if (filled(board.doc) && !window.confirm('Заменить текущую сетку примером?')) return;
  const mode = board.mode;
  const items = EXAMPLES[mode];
  board.replaceCells(items);
  const gen = board.generation;
  const find = mode === 'books' ? searchBooks : searchPeople;
  const norm = (t) => t.toLowerCase().replace(/ё/g, 'е');
  // по одному: у Apple ограничение около 20 запросов в минуту; если сетку очистили или заменили,
  // поиск дальше не продолжаем (generation сменился)
  for (let i = 0; i < items.length; i++) {
    if (gen !== board.generation) return;
    try {
      const list = await find(items[i].q);
      const ok = list.filter((r) => r.imgUrl && norm(r.title).includes(norm(items[i].title).split(' ')[0]));
      // Apple Books: обложки русских изданий; Open Library часто отдаёт переводы
      const hit = ok.find((r) => r.source === 'Apple Books') || ok[0];
      if (hit) await board.attachCover({ url: hit.imgUrl }, i, { silent: true });
    } catch { /* поиск не ответил: остаётся цветная плитка */ }
  }
}

// ---------------------------------------------------------------- PNG
async function onSavePng() {
  const btn = $('#save');
  btn.disabled = true;
  try {
    await fontsReady();
    const d = board.doc;
    await images.preload(d.cells.map((c) => c.img));
    const c = document.createElement('canvas');
    c.width = W; c.height = H;
    renderPoster(c.getContext('2d'), d, { theme: state.theme, selected: -1, hints: false });
    const blob = await new Promise((res) => c.toBlob(res, 'image/png'));
    if (!blob) throw new Error('Браузеру не хватило памяти на картинку. Закройте лишние вкладки и попробуйте ещё раз.');
    const a = el('a', { href: URL.createObjectURL(blob), download: `9-lyubimyh-${MODES[state.mode].hash}.png` });
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  } catch (e) {
    toast(e.message || 'Не удалось сохранить картинку.');
  } finally {
    btn.disabled = false;
  }
}

// ---------------------------------------------------------------- старт
window.addEventListener('hashchange', () => {
  const id = Object.keys(MODES).find((m) => '#' + MODES[m].hash === location.hash);
  if (id) board.setMode(id);
});
renderPanel();
draw();
(async () => {
  await fontsReady();
  await images.preload(board.doc.cells.map((c) => c.img));
  draw();
  document.documentElement.dataset.ready = '1';
})();
